/**
 * Case Studies Block
 * 
 * JavaScript for filtering case studies by use case and industry with AJAX
 */

/**
 * Cache of case study page URLs.
 * The key is the clean URL, the value is the HTML content.
 */
const caseStudyPageCache = new Map();
const caseStudyPageInFlight = new Map();
const caseStudyImagePreloaded = new Set();
const caseStudyImageInFlight = new Map();

let caseStudiesLoadController = null;
let userScrolledRecently = false;
let userScrollClearTimer = null;

/**
 * Preload a case study page and cache it.
 * @param {string} pageUrl - The URL of the page to load.
 * @returns {Promise<string>}
 */
async function preloadCaseStudyPage(pageUrl, fetchImages = true) {
	if (!pageUrl) throw new Error('Page URL is required');

	// Use the clean URL for the cache key to avoid unnecessary lookups.
	const cleanUrl = getCleanUrl(pageUrl).toString();

	if (caseStudyPageCache.has(cleanUrl)) {
		console.log('Cache hit for', cleanUrl, 'from', pageUrl);
		return caseStudyPageCache.get(cleanUrl);
	}
	if (caseStudyPageInFlight.has(cleanUrl)) {
		console.log('Cache pending for', cleanUrl, 'from', pageUrl);
		return caseStudyPageInFlight.get(cleanUrl);
	}

	const requestPromise = (async () => {
		// Fetch the raw HTML content from the full URL.
		const response = await fetch(pageUrl);
		const html = await response.text();
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');
		const caseStudiesContent = doc.querySelector('#case-studies-content');
		if (!caseStudiesContent || !caseStudiesContent?.innerHTML) {
			console.error('No content found in the fetched HTML');
			throw new Error('No content found in the fetched HTML');
		}

		const caseStudiesContentHtml = caseStudiesContent.innerHTML;

		caseStudyPageCache.set(cleanUrl, caseStudiesContentHtml);
		console.log('Cache miss for', cleanUrl, 'from', pageUrl);

		/* Preload the images. */
		if (fetchImages) {
			console.log('Preloading images for', cleanUrl, 'from', pageUrl);
			const imageUrls = extractImageUrls(caseStudiesContent, pageUrl);
			void Promise.allSettled(imageUrls.map(preloadImage));
		}

		return caseStudiesContentHtml;
	})().catch(error => {
		console.error('Error preloading case study page:', error);
		throw error;
	}).finally(() => {
		caseStudyPageInFlight.delete(cleanUrl);
	});

	caseStudyPageInFlight.set(cleanUrl, requestPromise);
	return requestPromise;
}

/**
 * Extract all image URLs from the case studies content element.
 * @param {Element} caseStudiesContent - The case studies content element.
 * @param {string} baseUrl - The base URL of the document.
 * @returns {string[]} The array of image URLs.
 */
function extractImageUrls(caseStudiesContent, baseUrl) {
	const urls = new Set();
	caseStudiesContent.querySelectorAll('img').forEach(img => {
		if (img.getAttribute('src')) {
			urls.add(new URL(img.getAttribute('src'), baseUrl).toString());
		}
		/* naive srcset handling: take all candidates */
		const srcset = img.getAttribute('srcset');
		if (srcset) {
			srcset.split(',').forEach(part => {
				const candidate = part.trim().split(/\s+/)[0];
				if (candidate) urls.add(new URL(candidate, baseUrl).toString());
			});
		}
	});
	return [...urls];
}

/**
 * Preload an image.
 * @param {string} url - The URL of the image to preload.
 * @returns {Promise<void>}
 */
function preloadImage(url) {
	if (caseStudyImagePreloaded.has(url)) {
		return Promise.resolve();
	}
	if (caseStudyImageInFlight.has(url)) {
		return caseStudyImageInFlight.get(url);
	}

	const imagePromise = new Promise(resolve => {
		const img = new Image();
		img.onload = img.onerror = () => {
			caseStudyImagePreloaded.add(url);
			caseStudyImageInFlight.delete(url);
			resolve();
		};
		img.src = url;
		console.log('Preloading image:', url);
	});

	caseStudyImageInFlight.set(url, imagePromise);
	return imagePromise;
}

/**
 * Filter the case studies by use case and industry.
 * @returns {void}
 */
function filterCaseStudies() {
	const useCase = document.getElementById('use-case-filter')?.value || '';
	const industry = document.getElementById('industry-filter')?.value || '';
	
	const contentContainer = document.getElementById('case-studies-content');
	const url = clearSearchParams( contentContainer?.getAttribute('data-base-url') ?? window.location.href );

	if (!url) return;

	if (useCase) {
		url.searchParams.set('use_case', useCase);
	}
	if (industry) {
		url.searchParams.set('industry', industry);
	}

	toggleClearFiltersButton(useCase || industry);
	pushCleanUrl(url);
	loadCaseStudiesFromUrl(url);

	return;
}

/**
 * Clear the filters and load the case studies from the URL.
 * @returns {void}
 */
function clearFilters() {
	// Reset dropdowns
	document.getElementById('use-case-filter').value = '';
	document.getElementById('industry-filter').value = '';
	
	const contentContainer = document.getElementById('case-studies-content');
	const url = clearSearchParams( contentContainer?.getAttribute('data-base-url') ?? window.location.href );

	if (!url) return;

	toggleClearFiltersButton(false);
	pushCleanUrl(url);
	loadCaseStudiesFromUrl(url);
}

/**
 * Load the case studies from the URL.
 * @param {URL} url 
 * @returns {void}
 */
function loadCaseStudiesFromUrl(url, scroll = true) {
	if (!url) return;

	const contentContainer = document.getElementById('case-studies-content');
	if (!contentContainer) return;

	// Abort any in-flight request so we don't get multiple scrollIntoView calls
	if (caseStudiesLoadController) {
		caseStudiesLoadController.abort();
	}
	caseStudiesLoadController = new AbortController();
	const signal = caseStudiesLoadController.signal;

	// Reset so scroll works for this user-initiated load (filter/pagination)
	if (scroll) userScrolledRecently = false;

	contentContainer.style.opacity = '0.5';
	contentContainer.style.pointerEvents = 'none';

	// Clone URL so we don’t mutate the browser URL
	const ajaxUrl = getAjaxUrl(url).toString();

	preloadCaseStudyPage(ajaxUrl)
		.then(html => {
			if (signal.aborted) return;
			if (!html) {
				console.error('No content found in the fetched HTML fragment');
				return;
			}
			contentContainer.innerHTML = html;

			attachPaginationHandlers();
		})
		.catch(error => {
			if (error.name === 'AbortError') return;
			console.error('Error loading case studies:', error);
		})
		.finally(() => {
			if (!signal.aborted) {
				contentContainer.style.opacity = '1';
				contentContainer.style.pointerEvents = 'auto';
			}
			if (scroll && !signal.aborted && !userScrolledRecently) {
				document.querySelector('#case-studies')?.scrollIntoView({ behavior: 'smooth' });
			}
		});
}

/**
 * Attach click handlers to the pagination links.
 */
function attachPaginationHandlers() {
	const paginationLinks = document.querySelectorAll('.case-studies-pagination__wrapper a');

	paginationLinks.forEach(link => {
		const href = link.getAttribute('href');

		if (!href) return;

		preloadCaseStudyPage(href, isDirectSibling(href));

		link.addEventListener('click', function (e) {
			e.preventDefault();
			const cleanUrl = pushCleanUrl(new URL(href, window.location.origin));
			
			this.setAttribute('href', cleanUrl.toString());
			loadCaseStudiesFromUrl(cleanUrl);
		});
	});
}

/**
 * Detect if a link is a direct sibling of the current page.
 * @param {string} href - The href of the link.
 * @returns {boolean} True if the link is a direct sibling of the current page, false otherwise.
 */
function isDirectSibling(href) {
	const hrefPaged = getPageNumber(href);
	const currentPaged = getPageNumber(window.location.href);
	return hrefPaged === currentPaged - 1 || hrefPaged === currentPaged + 1;
}

/**
 * Get page number from URL supporting both query and pretty permalink formats.
 * @param {string} urlString
 * @returns {number}
 */
function getPageNumber(urlString) {
	const absoluteUrl = new URL(urlString, window.location.origin);
	const pagedParam = Number(absoluteUrl.searchParams.get('paged'));
	if (Number.isFinite(pagedParam) && pagedParam > 0) return pagedParam;

	const pathMatch = absoluteUrl.pathname.match(/\/page\/(\d+)\/?$/);
	const pathPaged = pathMatch ? Number(pathMatch[1]) : 1;
	return Number.isFinite(pathPaged) && pathPaged > 0 ? pathPaged : 1;
}

/**
 * Get clean URL from pagination link.
 * @param {URL} maybeDirtyUrl 
 * @returns {URL} The clean URL.
 */
function getCleanUrl(maybeDirtyUrl) {
	const cleanUrl = new URL(maybeDirtyUrl.toString(), window.location.origin);
	cleanUrl.searchParams.delete('ajax');
	cleanUrl.searchParams.delete('per_page');
	return cleanUrl;
}

/**
 * Get AJAX URL from plain URL.
 * This is the URL that will be used to load the case studies from the server.
 * It includes the AJAX parameter and the per_page parameter.
 * @param {URL} plainUrl 
 * @returns {URL} The AJAX URL.
 */
function getAjaxUrl(plainUrl) {
	const ajaxUrl = new URL(plainUrl.toString());
	if (!ajaxUrl.searchParams.has('paged')) {
		ajaxUrl.searchParams.set('paged', '1');
	}
	ajaxUrl.searchParams.set('ajax', '1');
	ajaxUrl.searchParams.set('per_page', window.innerWidth >= 900 ? 6 : 3);
	return ajaxUrl;
}

/**
 * Strip Ajax from the URL and push to the browser history, return the clean URL.
 * @param {URL} maybeDirtyUrl 
 * @returns {URL} The clean URL.
 */
function pushCleanUrl(maybeDirtyUrl) {
	if (!maybeDirtyUrl) return;

	const cleanUrl = getCleanUrl(maybeDirtyUrl);
	window.history.pushState({}, '', cleanUrl);
	return cleanUrl;	
}

/**
 * If the URL is not absolute, prepend the window.location.origin to it.
 * @param {string} url 
 * @returns {URL} The absolute URL.
 */
function getAbsoluteUrl(url) {
	const absoluteUrl = url.startsWith('http://') || url.startsWith('https://') 
		? new URL(url) 
		: new URL(url, window.location.origin);

	// Remove pagination from path if present (pretty permalinks: /page/2/)
	absoluteUrl.pathname = absoluteUrl.pathname.replace(/\/page\/\d+\/?$/, '');

	// Ensure pathname ends with / if it's not empty
	if (absoluteUrl.pathname && !absoluteUrl.pathname.endsWith('/')) {
		absoluteUrl.pathname += '/';
	}

	return absoluteUrl;
}

/**
 * Convert the URL string to a URL object and remove use_case, industry, and paged search params.
 * @param {string} url 
 * @returns {URL} The URL with the search params removed.
 */
function clearSearchParams(url) {
	const cleanUrl = new URL(getAbsoluteUrl(url));
	cleanUrl.searchParams.delete('use_case');
	cleanUrl.searchParams.delete('industry');
	cleanUrl.searchParams.delete('paged');
	return cleanUrl;
}

/**
 * Toggle the visibility of the clear filters button.
 * If either use case or industry is selected, show the button.
 * @param {boolean} show - Whether to show the button.
 * @returns {void}
 */
function toggleClearFiltersButton(show = false) {
	let showButton = show;

	if (!showButton) {
		const useCase = document.getElementById('use-case-filter')?.value || '';
		const industry = document.getElementById('industry-filter')?.value || '';
		showButton = useCase || industry;
	}

	const clearFiltersButton = document.getElementById('clear-filters');
	if (clearFiltersButton) {
		clearFiltersButton.classList.toggle('visible', showButton);
	}
}

function onUserScroll() {
	userScrolledRecently = true;
	if (userScrollClearTimer) clearTimeout(userScrollClearTimer);
	userScrollClearTimer = setTimeout(() => {
		userScrolledRecently = false;
		userScrollClearTimer = null;
	}, 500);
}

document.addEventListener('DOMContentLoaded', function() {
	toggleClearFiltersButton(false);
	attachPaginationHandlers();

	const clearFiltersButton = document.getElementById('clear-filters');
	if (clearFiltersButton) {
		clearFiltersButton.addEventListener('click', function (e) {
			e.preventDefault();
			clearFilters();
		});
	}
	// Reload the case studies to show the correct number of case studies (without scrolling to the top).
	loadCaseStudiesFromUrl(window.location.href, false);

	// Skip scrollIntoView if user has manually scrolled (prevents automatic scroll from fighting/repeating)
	window.addEventListener('wheel', onUserScroll, { passive: true });
	window.addEventListener('touchmove', onUserScroll, { passive: true });

	// Preload the case study pages for the initial load.
	preloadCaseStudyPage(getAjaxUrl(window.location.href).toString());
});

// Make functions available globally
window.filterCaseStudies = filterCaseStudies;
window.clearFilters = clearFilters;
